// Lightweight local-first health store (no backend / no auth needed).
// Persists a rolling 30-day log of metrics and a profile in localStorage.

const KEY = "silentscan_v11_health_v1";

const DEFAULT = {
  profile: {
    name: "",
    dob: "",
    height: null, // cm
    weight: null, // kg
  },
  today: {
    // Overwritten on load if the last logged day != today
    date: "",
    steps: 0,
    water_ml: 0,
    sleep_hours: 0,
    heart_rate: null,
    blood_pressure: { systolic: null, diastolic: null },
    blood_sugar: null,
    spo2: null,
    temperature: null,
    mood: "", // 😀 🙂 😐 😔 😢
    weight: null,
    activity_min: 0,
    calories: 0,
  },
  history: [], // last 30 daily snapshots
  streak: 0,
  last_check_in: null,
  missions_done: {}, // { "2026-07-26": ["water", "walk"] }
};

const todayStr = () => new Date().toISOString().slice(0, 10);

export const loadHealth = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT, today: { ...DEFAULT.today, date: todayStr() } };
    const parsed = JSON.parse(raw);
    // Rollover: if today is a new day, archive the old "today" into history.
    const t = todayStr();
    if (parsed.today?.date && parsed.today.date !== t) {
      parsed.history = [parsed.today, ...(parsed.history || [])].slice(0, 30);
      parsed.today = { ...DEFAULT.today, date: t };
    }
    if (!parsed.today?.date) parsed.today = { ...DEFAULT.today, date: t };
    return { ...DEFAULT, ...parsed };
  } catch {
    return { ...DEFAULT, today: { ...DEFAULT.today, date: todayStr() } };
  }
};

export const saveHealth = (state) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
};

export const updateToday = (patch) => {
  const s = loadHealth();
  s.today = { ...s.today, ...patch };
  saveHealth(s);
  return s;
};

export const updateProfile = (patch) => {
  const s = loadHealth();
  s.profile = { ...s.profile, ...patch };
  saveHealth(s);
  return s;
};

export const toggleMission = (missionId) => {
  const s = loadHealth();
  const t = todayStr();
  const list = s.missions_done[t] || [];
  const has = list.includes(missionId);
  s.missions_done[t] = has ? list.filter((x) => x !== missionId) : [...list, missionId];
  // Update streak: check yesterday count
  if (!has) {
    // if user just completed 3+ missions today for the first time, bump streak
    if (s.missions_done[t].length === 3 && s.last_check_in !== t) {
      s.streak = (s.streak || 0) + 1;
      s.last_check_in = t;
    }
  }
  saveHealth(s);
  return s;
};

export const computeBMI = (heightCm, weightKg) => {
  if (!heightCm || !weightKg) return null;
  const m = heightCm / 100;
  return +(weightKg / (m * m)).toFixed(1);
};

export const bmiCategory = (bmi) => {
  if (bmi == null) return "";
  if (bmi < 18.5) return "Underweight";
  if (bmi < 25) return "Healthy";
  if (bmi < 30) return "Overweight";
  return "Obese";
};

export const goodMorning = () => {
  const h = new Date().getHours();
  if (h < 5) return "Still up?";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Good night";
};
